import { instance, getToken } from './config';
import { DEFAULT_SUBSCRIPT_NUMBER } from '../constants';

const createFolder = (projectId, instanceId, path, instanceSubscriptNumber) =>
  instance.post(
    `/projects/${projectId}/instance-sets/${instanceId}/files/dir/touch`,
    null,
    {
      headers: { Authorization: `Bearer ${getToken()}` },
      params: { path, instanceSubscriptNumber },
    }
  );

const renameFolderFile = (
  projectId,
  instanceId,
  oldPath,
  newPath,
  instanceSubscriptNumber
) =>
  instance.post(
    `/projects/${projectId}/instance-sets/${instanceId}/files/rename`,
    null,
    {
      headers: { Authorization: `Bearer ${getToken()}` },
      params: {
        instanceSubscriptNumber,
        oldPath,
        newPath,
      },
    }
  );

const deleteFolderFile = (
  projectId,
  instanceId,
  instanceSubscriptNumber = DEFAULT_SUBSCRIPT_NUMBER,
  basePath,
  names
) =>
  instance.delete(`/projects/${projectId}/instance-sets/${instanceId}/files`, {
    headers: { Authorization: `Bearer ${getToken()}` },
    params: {
      instanceSubscriptNumber,
      basePath: basePath,
      names: names,
    },
  });

const downloadFiles = (
  projectId,
  instanceId,
  isDownload,
  instanceSubscriptNumber = DEFAULT_SUBSCRIPT_NUMBER,
  basePath,
  names
) =>
  instance.get(`/projects/${projectId}/instance-sets/${instanceId}/files`, {
    responseType: isDownload ? 'arraybuffer' : '',
    headers: { Authorization: `Bearer ${getToken()}` },
    params: {
      instanceSubscriptNumber,
      basePath: basePath,
      names: names,
    },
  });

/**
 * Get log partition
 * @param projectId - project id
 * @param instanceId - instance id
 * @param instanceSubscriptNumber - instance subscript number
 * @param lastByte - Last byte requested in the previous request. If it is the first request, it should be 0.
 * @param chunkSize - Size of the chunk to be requested
 * @param basePath - Path to the file within the file manager
 * @param names - Since we are using the same endpoint for multiple files, we
 * need to pass the names of the files, but in this case it would be [oneFileName]
 * @returns {Promise<AxiosResponse<any>>}
 */
const getLogPartition = (
  projectId,
  instanceId,
  instanceSubscriptNumber = DEFAULT_SUBSCRIPT_NUMBER,
  lastByte,
  chunkSize,
  basePath,
  names
) =>
  instance.get(`/projects/${projectId}/instance-sets/${instanceId}/files`, {
    responseType: 'arraybuffer',
    headers: {
      Authorization: `Bearer ${getToken()}`,
      // X-Range header is used to request a specific range of bytes from the file.
      // This is a custom header, but works the same as Range header. At least
      // for the basic functionality due to, again, is a custom implementation in the server side.
      'X-Range':
        lastByte === 0
          ? `bytes=-${chunkSize}`
          : `bytes=${lastByte}-${lastByte + chunkSize - 1}`,
    },
    params: {
      instanceSubscriptNumber,
      basePath: basePath,
      names: names,
    },
  });

const getFileList = (
  projectId,
  instanceId,
  path,
  instanceSubscriptNumber = DEFAULT_SUBSCRIPT_NUMBER
) =>
  instance.post(
    `/projects/${projectId}/instance-sets/${instanceId}/files/list`,
    null,
    {
      headers: { Authorization: `Bearer ${getToken()}` },
      params: {
        instanceSubscriptNumber,
        path,
      },
    }
  );

const uploadFiles = (
  projectId,
  instanceId,
  instanceSubscriptNumber,
  basePath,
  body
) =>
  instance.post(
    `/projects/${projectId}/instance-sets/${instanceId}/files/reg`,
    body,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${getToken()}`,
      },
      params: {
        instanceSubscriptNumber,
        basePath: basePath,
      },
      timeout: 60000,
    }
  );

const uploadAndUnzipFiles = (
  projectId,
  instanceId,
  instanceSubscriptNumber,
  basePath,
  body
) =>
  instance.post(
    `/projects/${projectId}/instance-sets/${instanceId}/files/dir`,
    body,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${getToken()}`,
      },
      params: {
        instanceSubscriptNumber,
        basePath: basePath,
      },
      timeout: 60000,
    }
  );

export const fileManagerApis = {
  createFolder,
  deleteFolderFile,
  downloadFiles,
  getLogPartition,
  getFileList,
  renameFolderFile,
  uploadAndUnzipFiles,
  uploadFiles,
};
